import React, { useEffect, useState } from 'react';
import './MatchModal.css';

const MatchModal = ({ match, round, onClose, onPlayerClick, onWinnerUpdate, isAdmin, onScoreUpdate }) => {
  const [selectedFrame, setSelectedFrame] = useState(1);
  const [localMatch, setLocalMatch] = useState(match);

  // Update local match when prop changes
  useEffect(() => {
    setLocalMatch(match);
  }, [match]);

  // Validate round exists
  useEffect(() => {
    if (!round) {
      console.error('MatchModal: round prop is missing!', { match, round });
    }
  }, [round, match]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  if (!match) return null;

  const getMatchFormat = () => {
    if (round === 'final') return 'Best of 7';
    if (round === 'semiFinals') return 'Best of 5';
    return 'Best of 3';
  };

  const getRoundName = () => {
    if (round === 'final') return 'Final';
    if (round === 'semiFinals') return 'Semi Finals';
    if (round === 'quarterFinals') return 'Quarter Finals';
    if (round === 'groupOf8') return 'Group of 8';
    return 'Group of 16';
  };

  // Get frame scores from local match or initialize
  const getMaxFrames = () => {
    if (round === 'final') return 7;
    if (round === 'semiFinals') return 5;
    return 3;
  };

  const initializeFrameScores = () => {
    const maxFrames = getMaxFrames();
    return Array.from({ length: maxFrames }, (_, i) => ({
      frame: i + 1,
      player1Score: null,
      player2Score: null,
      winner: null
    }));
  };

  const frameScores = localMatch.frameScores && localMatch.frameScores.length > 0
    ? localMatch.frameScores
    : initializeFrameScores();

  const player1Wins = frameScores.filter(f => f.winner === 1).length;
  const player2Wins = frameScores.filter(f => f.winner === 2).length;

  return (
    <div className="match-modal-overlay" onClick={onClose}>
      <div className="match-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="match-modal-close" onClick={onClose}>×</button>
        
        <div className="match-modal-header">
          <h2 className="match-modal-title">Match {localMatch.id}</h2>
          <div className="match-modal-round">{getRoundName()}</div>
          <div className="match-modal-format">{getMatchFormat()}</div>
        </div>

        <div className="match-modal-players">
          <div 
            className={`match-modal-player ${localMatch.winner === 1 ? 'winner' : ''}`}
            onClick={() => onPlayerClick && onPlayerClick(localMatch.player1, localMatch, round, 1)}
          >
            <div className="match-modal-player-image-container">
              <img 
                src={localMatch.player1.image || '/images/test.jpeg'} 
                alt={localMatch.player1.name}
                className="match-modal-player-image"
                onError={(e) => {
                  e.target.src = '/images/test.jpeg';
                }}
              />
            </div>
            <div className="match-modal-player-name">{localMatch.player1.name}</div>
            <div className="match-modal-player-badge home-badge">HOME</div>
            {localMatch.winner === 1 && <div className="match-winner-badge">WINNER</div>}
          </div>

          <div className="match-modal-vs">VS</div>

          <div 
            className={`match-modal-player ${localMatch.winner === 2 ? 'winner' : ''}`}
            onClick={() => onPlayerClick && onPlayerClick(localMatch.player2, localMatch, round, 2)}
          >
            <div className="match-modal-player-image-container">
              <img 
                src={localMatch.player2.image || '/images/test.jpeg'} 
                alt={localMatch.player2.name}
                className="match-modal-player-image"
                onError={(e) => {
                  e.target.src = '/images/test.jpeg';
                }}
              />
            </div>
            <div className="match-modal-player-name">{localMatch.player2.name}</div>
            <div className="match-modal-player-badge away-badge">AWAY</div>
            {localMatch.winner === 2 && <div className="match-winner-badge">WINNER</div>}
          </div>
        </div>

        <div className="match-modal-score-summary">
          <div className="score-summary-item">
            <span className="score-label">{localMatch.player1.name}</span>
            <span className="score-value">{player1Wins}</span>
          </div>
          <div className="score-divider">-</div>
          <div className="score-summary-item">
            <span className="score-label">{localMatch.player2.name}</span>
            <span className="score-value">{player2Wins}</span>
          </div>
        </div>

        <div className="match-modal-frames">
          <h3 className="frames-title">Frame Scores</h3>
          <div className="frames-list">
            {frameScores.map((frame, index) => (
              <div key={index} className="frame-item">
                <div className="frame-number">Frame {frame.frame}</div>
                <div className="frame-scores">
                  <span className={`frame-score ${frame.winner === 1 ? 'winner' : ''}`}>
                    {frame.player1Score !== null ? frame.player1Score : '-'}
                  </span>
                  <span className="frame-separator">-</span>
                  <span className={`frame-score ${frame.winner === 2 ? 'winner' : ''}`}>
                    {frame.player2Score !== null ? frame.player2Score : '-'}
                  </span>
                </div>
                {frame.winner && (
                  <div className="frame-winner">
                    {frame.winner === 1 ? match.player1.name : match.player2.name} wins
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {match.matchDate && (
          <div className="match-modal-info">
            <div className="info-item">
              <span className="info-label">Date:</span>
              <span className="info-value">{match.matchDate}</span>
            </div>
            {match.matchTime && (
              <div className="info-item">
                <span className="info-label">Time:</span>
                <span className="info-value">{match.matchTime}</span>
              </div>
            )}
            {match.venue && (
              <div className="info-item">
                <span className="info-label">Venue:</span>
                <span className="info-value">{match.venue}</span>
              </div>
            )}
          </div>
        )}

        {match.highestBreak && (
          <div className="match-modal-breaks">
            <div className="break-item">
              <span className="break-label">Highest Break:</span>
              <span className="break-value">{match.highestBreak.points}</span>
              <span className="break-player">by {match.highestBreak.player}</span>
            </div>
          </div>
        )}

        {isAdmin && onScoreUpdate && (
          <div className="match-admin-controls">
            <h3 className="admin-section-title">Admin Controls</h3>
            
            <div className="frame-scores-admin">
              <h4 className="frame-scores-title">Frame Scoring</h4>
              
              {/* Frame Selector */}
              <div className="frame-selector">
                <label>Select Frame:</label>
                <select 
                  value={selectedFrame} 
                  onChange={(e) => setSelectedFrame(parseInt(e.target.value))}
                  className="frame-select"
                >
                  {frameScores.map((frame) => (
                    <option key={frame.frame} value={frame.frame}>
                      Frame {frame.frame}
                    </option>
                  ))}
                </select>
              </div>

              {/* Current Frame Display */}
              {(() => {
                const currentFrame = frameScores.find(f => f.frame === selectedFrame) || frameScores[selectedFrame - 1];
                const currentFrameIndex = frameScores.findIndex(f => f.frame === selectedFrame);
                
                const handleScoreIncrement = (player, points) => {
                  const newScores = [...frameScores];
                  const frameIndex = currentFrameIndex >= 0 ? currentFrameIndex : selectedFrame - 1;
                  
                  if (player === 1) {
                    const currentScore = newScores[frameIndex].player1Score || 0;
                    const newScore = Math.max(0, currentScore + points); // Prevent negative scores
                    newScores[frameIndex].player1Score = newScore;
                  } else {
                    const currentScore = newScores[frameIndex].player2Score || 0;
                    const newScore = Math.max(0, currentScore + points); // Prevent negative scores
                    newScores[frameIndex].player2Score = newScore;
                  }
                  
                  // Update local state immediately for instant UI update
                  setLocalMatch(prevMatch => ({
                    ...prevMatch,
                    frameScores: newScores
                  }));
                  
                  // Sync with server in background
                  onScoreUpdate(localMatch, round, { frameScores: newScores });
                };

                const handleSetWinner = (winner) => {
                  const newScores = [...frameScores];
                  const frameIndex = currentFrameIndex >= 0 ? currentFrameIndex : selectedFrame - 1;
                  newScores[frameIndex].winner = currentFrame.winner === winner ? null : winner;
                  
                  // Update local state immediately
                  setLocalMatch(prevMatch => ({
                    ...prevMatch,
                    frameScores: newScores
                  }));
                  
                  // Sync with server in background
                  onScoreUpdate(localMatch, round, { frameScores: newScores });
                };

                return (
                  <div className="current-frame-scoring">
                    <div className="frame-current-info">
                      <h5>Frame {selectedFrame} - Running Totals</h5>
                      <div className="frame-current-scores">
                        <div className="player-score-display">
                          <span className="player-label">{localMatch.player1.name}</span>
                          <span className="score-display-value">{currentFrame.player1Score || 0}</span>
                          <span className="score-hint">(Max: 147)</span>
                        </div>
                        <span className="score-separator-large">-</span>
                        <div className="player-score-display">
                          <span className="player-label">{localMatch.player2.name}</span>
                          <span className="score-display-value">{currentFrame.player2Score || 0}</span>
                          <span className="score-hint">(Max: 147)</span>
                        </div>
                      </div>
                      <div className="scoring-instructions">
                        <p>Click +1 to +7 to add points per shot. Use -1 to decrease score if needed. Players can score continuously until they miss.</p>
                      </div>
                    </div>

                    {/* Player 1 Increment Buttons */}
                    <div className="score-increment-section">
                      <div className="player-increment-group">
                        <div className="player-increment-label">{localMatch.player1.name}</div>
                        <div className="increment-buttons">
                          <button
                            key="p1--1"
                            className="increment-btn decrement-btn"
                            onClick={() => handleScoreIncrement(1, -1)}
                            title="Decrease score by 1"
                          >
                            -1
                          </button>
                          {[1, 2, 3, 4, 5, 6, 7].map((points) => (
                            <button
                              key={`p1-${points}`}
                              className="increment-btn"
                              onClick={() => handleScoreIncrement(1, points)}
                            >
                              +{points}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Player 2 Increment Buttons */}
                      <div className="player-increment-group">
                        <div className="player-increment-label">{localMatch.player2.name}</div>
                        <div className="increment-buttons">
                          <button
                            key="p2--1"
                            className="increment-btn decrement-btn"
                            onClick={() => handleScoreIncrement(2, -1)}
                            title="Decrease score by 1"
                          >
                            -1
                          </button>
                          {[1, 2, 3, 4, 5, 6, 7].map((points) => (
                            <button
                              key={`p2-${points}`}
                              className="increment-btn"
                              onClick={() => handleScoreIncrement(2, points)}
                            >
                              +{points}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Frame Winner Buttons */}
                    <div className="frame-winner-selector">
                      <button
                        className={`frame-winner-btn-large ${currentFrame.winner === 1 ? 'selected' : ''}`}
                        onClick={() => handleSetWinner(1)}
                      >
                        {localMatch.player1.name} Wins Frame
                      </button>
                      <button
                        className={`frame-winner-btn-large ${currentFrame.winner === 2 ? 'selected' : ''}`}
                        onClick={() => handleSetWinner(2)}
                      >
                        {localMatch.player2.name} Wins Frame
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="match-winner-selector">
              <h3 className="winner-selector-title">Set Match Winner</h3>
              <div className="winner-buttons">
                <button
                  className={`winner-button ${localMatch.winner === 1 ? 'selected' : ''}`}
                  onClick={() => {
                    if (!round) {
                      console.error('Cannot update winner: round is missing', { match: localMatch, round });
                      return;
                    }
                    onWinnerUpdate(localMatch, round, 1);
                  }}
                >
                  {localMatch.player1.name} Wins
                </button>
                <button
                  className={`winner-button ${localMatch.winner === 2 ? 'selected' : ''}`}
                  onClick={() => {
                    if (!round) {
                      console.error('Cannot update winner: round is missing', { match: localMatch, round });
                      return;
                    }
                    onWinnerUpdate(localMatch, round, 2);
                  }}
                >
                  {localMatch.player2.name} Wins
                </button>
                {localMatch.winner !== null && (
                  <button
                    className="winner-button clear"
                    onClick={() => {
                      if (!round) {
                        console.error('Cannot update winner: round is missing', { match: localMatch, round });
                        return;
                      }
                      onWinnerUpdate(localMatch, round, null);
                    }}
                  >
                    Clear Winner
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchModal;
